/**
 * Copyright 2025 Lumavec Group LLC
 *
 * Velocity PR dialog - create PR from velocity to default branch
 */

import React from 'react';
import { GitPullRequest, Loader, AlertCircle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import type { SourceControlProvider } from '../types';

interface VelocityPRDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  prTitle: string;
  onPrTitleChange: (title: string) => void;
  prBody: string;
  onPrBodyChange: (body: string) => void;
  onCreatePR: () => void;
  creatingPR: boolean;
  sourceControlProvider: SourceControlProvider;
  /** Target branch name from organization settings (defaults to 'main') */
  defaultBranch?: string;
  /** Error message to display in the dialog */
  error?: string | null;
  /** Callback to clear the error when user edits fields */
  onClearError?: () => void;
}

/**
 * Generate a unique PR title with timestamp
 */
export function generateUniquePRTitle(productTitle?: string, defaultBranch: string = 'main'): string {
  const now = new Date();
  const date = now.toISOString().split('T')[0]; // YYYY-MM-DD
  const time = now.toTimeString().split(' ')[0].slice(0, 5).replace(':', ''); // HHMM
  
  if (productTitle) {
    return `[${productTitle}] Product Data Sync - ${date} ${time}`;
  }
  return `Product Data Sync - ${date} ${time}`;
}

export function VelocityPRDialog({
  isOpen,
  onOpenChange,
  prTitle,
  onPrTitleChange,
  prBody,
  onPrBodyChange,
  onCreatePR,
  creatingPR,
  sourceControlProvider,
  defaultBranch = 'main',
  error,
  onClearError
}: VelocityPRDialogProps) {
  const prLabel = sourceControlProvider === 'github' ? 'Pull Request' : 'Merge Request';
  const prShortLabel = sourceControlProvider === 'github' ? 'PR' : 'MR';
  
  // Clear error when user edits title
  const handleTitleChange = (value: string) => {
    if (error && onClearError) {
      onClearError();
    }
    onPrTitleChange(value);
  };
  
  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Create {prLabel} to {defaultBranch}</AlertDialogTitle>
          <AlertDialogDescription>
            Promote velocity branch changes to the {defaultBranch} branch. This will push velocity to remote and create a {prShortLabel}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <div className="space-y-4 my-4">
          {/* Error message banner */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm flex items-start gap-2">
              <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-900">Error creating {prShortLabel}</p>
                <p className="text-red-700 mt-1">{error}</p>
                {error.toLowerCase().includes('already exists') && (
                  <p className="text-red-600 mt-2 text-xs">
                    Try changing the title to make it unique, or close an existing {prShortLabel} first.
                  </p>
                )}
              </div>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={prTitle}
              onChange={(e) => handleTitleChange(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md ${
                error?.toLowerCase().includes('title') || error?.toLowerCase().includes('already exists')
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300'
              }`}
              placeholder={generateUniquePRTitle(undefined, defaultBranch)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={prBody}
              onChange={(e) => onPrBodyChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              rows={4}
              placeholder="Merging product specification changes from velocity branch..."
            />
          </div>
          
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
            <p className="font-medium text-blue-900">This will:</p>
            <ul className="mt-1 text-blue-800 text-xs space-y-1 ml-4 list-disc">
              <li>Push velocity branch to remote (origin/velocity)</li>
              <li>Create a {prLabel.toLowerCase()} from velocity → {defaultBranch}</li>
              <li>Open the {prShortLabel} in your browser</li>
            </ul>
          </div>
        </div>
        
        <AlertDialogFooter>
          <AlertDialogCancel disabled={creatingPR}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onCreatePR();
            }}
            disabled={!prTitle.trim() || creatingPR}
          >
            {creatingPR ? (
              <>
                <Loader size={14} className="animate-spin mr-2" />
                Creating...
              </>
            ) : (
              <>
                <GitPullRequest size={14} className="mr-2" />
                {error ? 'Retry' : `Create ${prShortLabel}`}
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

